import { ROLES, type Role } from '@playplate/shared';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { KeyRound, Plus, UserX } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { ErrorState, Loading, PageHeader, Panel } from '../components/ui';
import { useAuth } from '../hooks/useAuth';
import { api, ApiError } from '../lib/api';
import { formatRelative } from '../lib/format';

type UserRow = {
  id: string;
  email: string;
  name: string;
  role: Role;
  phoneE164: string | null;
  active: boolean;
  mustChangePassword: boolean;
  lastLoginAt: string | null;
};

const ROLE_DESCRIPTIONS: Record<Role, string> = {
  OWNER: 'Everything, including user management and system settings',
  ADMIN: 'Full operational control and configuration',
  MANAGER: 'All zones; acknowledges and resolves alerts',
  SUPERVISOR: 'Floor zones only — no billing, kitchen or storage',
  VIEWER: 'Read-only, dining and gaming zones',
};

export function UsersPage() {
  const { user: me, can } = useAuth();
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);

  const usersQuery = useQuery({
    queryKey: ['users'],
    queryFn: () => api.get<{ items: UserRow[] }>('/users'),
  });

  const update = useMutation({
    mutationFn: ({ id, body }: { id: string; body: Record<string, unknown> }) =>
      api.patch(`/users/${id}`, body),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['users'] }),
  });

  const resetPassword = useMutation({
    mutationFn: ({ id, password }: { id: string; password: string }) =>
      api.post(`/users/${id}/reset-password`, { password }),
  });

  if (usersQuery.isLoading) return <Loading label="Loading users" />;
  if (usersQuery.isError) {
    return <ErrorState message={(usersQuery.error as Error).message} onRetry={() => void usersQuery.refetch()} />;
  }

  const canManage = can('user:manage');

  return (
    <div>
      <PageHeader
        title="Users & Roles"
        subtitle="Roles control which zones a person can see, not just which buttons they get."
        actions={
          canManage ? (
            <button type="button" className="btn-primary" onClick={() => setCreating(true)}>
              <Plus className="h-4 w-4" aria-hidden />
              Add user
            </button>
          ) : null
        }
      />

      {creating ? <CreateUserForm onClose={() => setCreating(false)} /> : null}

      <Panel className="mb-6">
        <div className="overflow-x-auto">
          <table className="w-full min-w-[720px]">
            <thead>
              <tr>
                <th className="table-header">Name</th>
                <th className="table-header">Role</th>
                <th className="table-header">Contact</th>
                <th className="table-header">Last sign-in</th>
                <th className="table-header">Status</th>
                {canManage ? <th className="table-header w-px" /> : null}
              </tr>
            </thead>
            <tbody>
              {usersQuery.data!.items.map((row) => {
                const isSelf = row.id === me?.id;
                return (
                  <tr key={row.id}>
                    <td className="table-cell">
                      <p className="font-semibold">
                        {row.name}
                        {isSelf ? <span className="ml-2 text-xs text-muted">(you)</span> : null}
                      </p>
                      <p className="text-xs text-muted">{row.email}</p>
                    </td>
                    <td className="table-cell">
                      {canManage && !isSelf ? (
                        <select
                          className="input w-auto py-1 text-xs"
                          value={row.role}
                          onChange={(e) =>
                            update.mutate({ id: row.id, body: { role: e.target.value } })
                          }
                        >
                          {ROLES.map((role) => (
                            <option key={role} value={role}>
                              {role}
                            </option>
                          ))}
                        </select>
                      ) : (
                        <span>{row.role}</span>
                      )}
                      <p className="mt-1 max-w-[220px] text-xs text-muted">{ROLE_DESCRIPTIONS[row.role]}</p>
                    </td>
                    <td className="table-cell font-mono text-xs text-muted">
                      {row.phoneE164 ?? <span className="text-high">no phone — no WhatsApp alerts</span>}
                    </td>
                    <td className="table-cell text-xs text-muted">{formatRelative(row.lastLoginAt)}</td>
                    <td className="table-cell text-xs">
                      {row.active ? (
                        <span className="text-online">Active</span>
                      ) : (
                        <span className="text-critical">Disabled</span>
                      )}
                      {row.mustChangePassword ? (
                        <span className="mt-0.5 block text-muted">Must change password</span>
                      ) : null}
                    </td>
                    {canManage ? (
                      <td className="table-cell">
                        <div className="flex gap-1">
                          <button
                            type="button"
                            className="btn-ghost p-2"
                            title="Reset password"
                            onClick={() => {
                              const password = window.prompt(
                                `Set a new password for ${row.name}. Minimum 12 characters. They will be required to change it at next sign-in.`,
                              );
                              if (password) {
                                resetPassword.mutate(
                                  { id: row.id, password },
                                  {
                                    onError: (e) =>
                                      window.alert(
                                        e instanceof ApiError ? e.message : 'Could not reset the password',
                                      ),
                                    onSuccess: () =>
                                      window.alert('Password reset. All their sessions were signed out.'),
                                  },
                                );
                              }
                            }}
                          >
                            <KeyRound className="h-4 w-4" />
                          </button>
                          {!isSelf ? (
                            <button
                              type="button"
                              className="btn-ghost p-2 text-critical hover:bg-critical/10"
                              title={row.active ? 'Disable account' : 'Enable account'}
                              onClick={() =>
                                update.mutate({ id: row.id, body: { active: !row.active } })
                              }
                            >
                              <UserX className="h-4 w-4" />
                            </button>
                          ) : null}
                        </div>
                      </td>
                    ) : null}
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Panel>

      <NotificationPreferences />
    </div>
  );
}

function CreateUserForm({ onClose }: { onClose: () => void }) {
  const queryClient = useQueryClient();
  const [form, setForm] = useState({ email: '', name: '', role: 'VIEWER' as Role, password: '', phoneE164: '' });
  const [error, setError] = useState<string | null>(null);

  const create = useMutation({
    mutationFn: () =>
      api.post('/users', {
        ...form,
        phoneE164: form.phoneE164 || null,
      }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['users'] });
      onClose();
    },
    onError: (e) => setError(e instanceof ApiError ? e.message : 'Could not create the user'),
  });

  const submit = (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    create.mutate();
  };

  return (
    <Panel title="Add user" className="mb-6">
      <form onSubmit={submit} className="grid gap-4 p-4 md:grid-cols-2">
        <label className="block">
          <span className="label mb-1.5 block">Name</span>
          <input
            className="input"
            required
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="label mb-1.5 block">Email</span>
          <input
            type="email"
            className="input"
            required
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
          />
        </label>
        <label className="block">
          <span className="label mb-1.5 block">Role</span>
          <select
            className="input"
            value={form.role}
            onChange={(e) => setForm({ ...form, role: e.target.value as Role })}
          >
            {ROLES.map((role) => (
              <option key={role} value={role}>
                {role} — {ROLE_DESCRIPTIONS[role]}
              </option>
            ))}
          </select>
        </label>
        <label className="block">
          <span className="label mb-1.5 block">Phone (E.164)</span>
          <input
            className="input font-mono"
            placeholder="+919876543210"
            value={form.phoneE164}
            onChange={(e) => setForm({ ...form, phoneE164: e.target.value })}
          />
          <span className="mt-1 block text-xs text-muted">Required for WhatsApp alerts.</span>
        </label>
        <label className="block md:col-span-2">
          <span className="label mb-1.5 block">Temporary password</span>
          <input
            type="password"
            className="input"
            required
            minLength={12}
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
          />
          <span className="mt-1 block text-xs text-muted">
            At least 12 characters. They must change it at first sign-in.
          </span>
        </label>

        {error ? (
          <p className="rounded-lg border border-critical/40 bg-critical/10 px-3 py-2 text-sm text-critical md:col-span-2">
            {error}
          </p>
        ) : null}

        <div className="flex gap-2 md:col-span-2">
          <button type="submit" className="btn-primary" disabled={create.isPending}>
            {create.isPending ? 'Creating…' : 'Create user'}
          </button>
          <button type="button" className="btn-secondary" onClick={onClose}>
            Cancel
          </button>
        </div>
      </form>
    </Panel>
  );
}

type Preference = {
  channel: string;
  enabled: boolean;
  minSeverity: string;
  quietHoursStart: string | null;
  quietHoursEnd: string | null;
  criticalOverridesQuietHours: boolean;
};

function NotificationPreferences() {
  const queryClient = useQueryClient();
  const prefsQuery = useQuery({
    queryKey: ['me', 'preferences'],
    queryFn: () => api.get<{ preferences: Preference[] }>('/me/preferences'),
  });

  const save = useMutation({
    mutationFn: (preferences: Preference[]) => api.put('/me/preferences', { preferences }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: ['me', 'preferences'] }),
  });

  const [draft, setDraft] = useState<Preference[] | null>(null);
  const preferences = draft ?? prefsQuery.data?.preferences ?? [];

  const patch = (channel: string, changes: Partial<Preference>) => {
    setDraft(preferences.map((p) => (p.channel === channel ? { ...p, ...changes } : p)));
  };

  if (prefsQuery.isLoading) return <Loading label="Loading notification preferences" />;

  return (
    <Panel title="My notification preferences">
      <div className="divide-y divide-line/50">
        {preferences.map((pref) => (
          <div key={pref.channel} className="grid gap-3 p-4 sm:grid-cols-[160px_1fr] sm:items-center">
            <div>
              <p className="text-sm font-semibold">{pref.channel}</p>
              {pref.channel === 'WHATSAPP' ? (
                <p className="text-xs text-muted">Requires a phone number on your account.</p>
              ) : null}
            </div>

            <div className="flex flex-wrap items-center gap-4">
              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-lime"
                  checked={pref.enabled}
                  onChange={(e) => patch(pref.channel, { enabled: e.target.checked })}
                />
                Enabled
              </label>

              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted">From</span>
                <select
                  className="input w-auto py-1 text-xs"
                  value={pref.minSeverity}
                  onChange={(e) => patch(pref.channel, { minSeverity: e.target.value })}
                >
                  {['LOW', 'MEDIUM', 'HIGH', 'CRITICAL'].map((severity) => (
                    <option key={severity} value={severity}>
                      {severity}
                    </option>
                  ))}
                </select>
              </label>

              <label className="flex items-center gap-2 text-sm">
                <span className="text-muted">Quiet hours</span>
                <input
                  type="time"
                  className="input w-auto py-1 text-xs"
                  value={pref.quietHoursStart ?? ''}
                  onChange={(e) =>
                    patch(pref.channel, {
                      quietHoursStart: e.target.value || null,
                      // Both ends must be set or neither; a half-configured
                      // window silently swallows notifications.
                      quietHoursEnd: e.target.value ? (pref.quietHoursEnd ?? '07:00') : null,
                    })
                  }
                />
                <span className="text-muted">to</span>
                <input
                  type="time"
                  className="input w-auto py-1 text-xs"
                  value={pref.quietHoursEnd ?? ''}
                  onChange={(e) =>
                    patch(pref.channel, {
                      quietHoursEnd: e.target.value || null,
                      quietHoursStart: e.target.value ? (pref.quietHoursStart ?? '22:00') : null,
                    })
                  }
                />
              </label>

              <label className="flex items-center gap-2 text-sm">
                <input
                  type="checkbox"
                  className="h-4 w-4 accent-lime"
                  checked={pref.criticalOverridesQuietHours}
                  onChange={(e) => patch(pref.channel, { criticalOverridesQuietHours: e.target.checked })}
                />
                Critical alerts ignore quiet hours
              </label>
            </div>
          </div>
        ))}
      </div>

      <div className="border-t border-line p-4">
        <button
          type="button"
          className="btn-primary"
          disabled={draft === null || save.isPending}
          onClick={() => {
            if (draft) save.mutate(draft, { onSuccess: () => setDraft(null) });
          }}
        >
          {save.isPending ? 'Saving…' : 'Save preferences'}
        </button>
      </div>
    </Panel>
  );
}
